import React, { useState, useEffect, useMemo, useRef, useReducer, useCallback, version} from "react";
import axios, { AxiosProgressEvent } from "axios";
import { Layout } from 'react-grid-layout';

import { useQuery, useMutation, useQueryClient, useQueries, UseQueryResult} from 'react-query';
import { forEach, isEqual, set } from 'lodash';
import { debounce } from 'lodash';
import getCookie from '../utilities/Cookie';
import { generateHeatmapV2, generateBarPlot, 
        generateBoxPlot, generateScatterPlot, updatePlotLayout, setBoxPlotTicks, generateTiledPlots, generateDataVsDataScatterPlot,
        extractDataProcessing,
        generateProcessedDataList,
        computePlotPValues,
        computeErrorBars,
        filterCategories
} from '../utilities/plotUtils';
import { data, time } from "@tensorflow/tfjs";
import { alertClasses, setRef } from "@mui/material";
import { deepEqual, getAxisFiltersFromSelectedItems, isObject, getPlotLayersMinMax } from '../utilities/utils';
import { color } from "@mui/system";

interface PageData {
    [page: number]: {
      layout: Layout[];
      content_ids: number[];
    };
}
  

const initialState = {
    plotLayers: [],
    plotLayout: {},
    tileTitleList: [],
    loading: false,
    plotPvalues: undefined,
    plotAlerts: [],
    error: null
};


function plotReducer(state: any, action: any) {
    const { plotIndex, ...payload } = action.payload;
    switch (action.type) {
        case 'UPDATE_PLOT':
            return {
                ...state,
                plots: state.plots.map((plot: any, index: number) =>
                    index === plotIndex ? {
                        ...plot,
                        plotLayers: payload.layers !== undefined ? payload.layers : plot.plotLayers,
                        plotLayout: payload.layout !== undefined ? payload.layout : plot.plotLayout,
                        tileTitleList: payload.tileTitleList !== undefined ? payload.tileTitleList : plot.tileTitleList,
                        loading: false
                    } : plot
                )
            };
        case 'SET_LOADING':
            return {
                ...state,
                plots: state.plots.map((plot: any, index: number) =>
                    index === plotIndex ? { ...plot, loading: payload.loading } : plot
                )
            };
        case 'SET_PLOT_PVALUES':
            return {
                ...state,
                plots: state.plots.map((plot: any, index: number) =>
                    index === plotIndex ? { ...plot, plotPvalues: payload.pvalues } : plot
                )
            };
        case 'SET_PLOT_ALERTS':
            return {
                ...state,
                plots: state.plots.map((plot: any, index: number) =>
                    index === plotIndex ? { ...plot, plotAlerts: payload.alerts } : plot
                )
            };
        case 'SET_ERROR':
            return {
                ...state,
                plots: state.plots.map((plot: any, index: number) =>
                    index === plotIndex ? { ...plot, error: payload.error, loading: false } : plot
                )
            };
        default:
            return state;
    }
}


function useDeepCompareMemoize<T>(value: T): T {
  const ref = useRef<T | undefined>(undefined);
  
  if (ref.current === undefined || !isEqual(value, ref.current)) {
    ref.current = value;
  }
  
  return ref.current as T; // Cast is safe here, since it's either the initial or a deeply equal value
}

export function useDeepCompareMemo<T>(factory: () => T, deps: any): T {
    const stableDeps = useDeepCompareMemoize(deps);
    return useMemo(factory, [stableDeps]);
  }
  

// Define a generic type for the hook
export const useTableItems = <T>() => {
    const [selectedItems, setSelectedItems] = useState<{ [key: string]: T[] }>({});

    const setItem = (label: string, index: number, value: T) => {
        setSelectedItems(prev => {
            const updatedItems = structuredClone(prev[label]);
            updatedItems[index] = value;
            return { ...prev, [label]: updatedItems };
        });
    };

    const addItem = (label: string, items: T[], startIndex?: number) => {
        setSelectedItems(prev => {
            const currentItems = prev[label] || [];
            if (startIndex !== undefined) {
                currentItems.splice(startIndex, items.length, ...items);
            } else {
                currentItems.push(...items);
            }
            return { ...prev, [label]: currentItems };
        });
    };

    const replaceItem = (label: string, index: number, value: T) => {
        setSelectedItems(prev => {
            const updatedItems = [...(prev[label] || [])];
            updatedItems[index] = value;
            return { ...prev, [label]: updatedItems };
        });
    }

    const removeItem = (label: string, index: number) => {
        setSelectedItems(prev => {
            const updatedItems = [...(prev[label] || [])];
            updatedItems.splice(index, 1);
            return { ...prev, [label]: updatedItems };
        });
    };

    const clearItems = (label: string) => {
        setSelectedItems(prev => {
            const updatedState = { ...prev };
            delete updatedState[label];
            return updatedState;
        });
    };

    const setItems = (newItems: { [key: string]: T[] }) => {
        setSelectedItems(newItems);
    };

    return { selectedItems, setItem, addItem, removeItem, clearItems, setItems, replaceItem };
};


export const useDropdowns = (sample: number[], refresh: number) => {
    const queryClient = useQueryClient(); // Use queryClient for managing cache

    const fetchDropdowns = async () => {
        const sampleQueryString = sample.join(',');

        // Check for cached data first to avoid unnecessary API calls
        const cachedData = queryClient.getQueryData(['get_selections_for_samples', sampleQueryString]);
        if (cachedData && !refresh) {
            return cachedData;
        }


        const clear_cache = refresh ? 'true' : 'false';
        const noCache = refresh ? `&nocache=${clear_cache}` : '';

        const response = await axios.get(`/tumors/api/v1/get_selections_for_samples/?samples=${sampleQueryString}${noCache}`);
        if (!response.data) {
            throw new Error("Unexpected data format received from server.");
        }

        return response.data;
    };

    const { data, isError, error } = useQuery(['get_selections_for_samples', sample.join(','), refresh], fetchDropdowns, {
        refetchOnWindowFocus: false,
        enabled: sample.length > 0,
        retry: 1,
        keepPreviousData: true, 
    });


    const dropdowns: Dropdowns = useMemo(() => ({
        versionTypeDict: data?.version_types || {},
        timepointDict: data?.timepoints || {},
        treatmentDict: data?.treatments || {},
        dataPipelineDict: data?.data_pipelines || {},
        versionTypes: data?.versionTypes || [],
    }), [data]);

    return {
        ...dropdowns,
        isError,
        error
    };
};

export const usePartitions = (updatePartitionCallback: (data?: any) => void, updateSampleGroupsCallback:(data?: any) => void) => {
    const queryClient = useQueryClient();
  
    const saveNewPartition = useMutation(async ({ name, study }: { name: string, study: number }) => {
      const csrfToken = getCookie("csrftoken"); // Ensure getCookie is defined or imported
      return axios.post('/tumors/api/v1/partitions', { name, study }, {
        headers: {
          "X-CSRFToken": csrfToken,
        },
      });
    }, {
      onSuccess: (data) => {
        queryClient.invalidateQueries('partitions');
        updatePartitionCallback(data['data']);
      },
      onError: (error) => {
        console.error("Failed to create plot group", error);
        // Optionally, handle the error more gracefully, e.g., displaying a user-friendly message
      }
    });
  
    const addNewPartition = (name: string, study: number) => {
      saveNewPartition.mutateAsync({ name, study });
    };
  
    // Delete partition
    const deletePartition = useMutation(async (id: number) => {
      const csrfToken = getCookie("csrftoken"); // Ensure getCookie is defined or imported
      await axios.delete(`/tumors/api/v1/partition/${id}`, {
        headers: {
          "X-CSRFToken": csrfToken,
        },
      });
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries('partitions');
        updatePartitionCallback();
      },
    });
  
    // Update partition
    const updatePartition = useMutation(async ({ 
      id, 
      name, 
      study, 
      sampleGroups,
      sampleGroupColors,
    }: { 
      id: number, 
      name?: string, 
      study?: number, 
      sampleGroups: Record<string, number[]> 
      sampleGroupColors: Record<string, string>
    }) => {
      const csrfToken = getCookie("csrftoken"); // Ensure getCookie is defined or imported
      
      // Transform sampleGroups into the format expected by the API
      const transformedSampleGroups = Object.keys(sampleGroups).map((key: string) => ({tumor_ids: sampleGroups[key], name: key, color: sampleGroupColors[key]}));
    
      return axios.put(`/tumors/api/v1/partition/${id}`, { 
        name, 
        study, 
        sample_groups: transformedSampleGroups,
      }, {
        headers: {
          "X-CSRFToken": csrfToken,
        },
      });
    }, {
      onSuccess: (data) => {
        queryClient.invalidateQueries('partitions');
        updateSampleGroupsCallback(data['data']);
      },
      onError: (error) => {
        console.error("Failed to update partition", error);
        // Optionally, handle the error more gracefully, e.g., displaying a user-friendly message
      }
    });
  
    return {
      add: addNewPartition,
      delete: deletePartition.mutateAsync,
      update: updatePartition.mutateAsync,
    };
  }


export const usePathways = () => {
    const queryClient = useQueryClient();
  
    const fetchPathways = async () => {
        const userPathways: Groups = (await axios.get('/tumors/api/v1/pathways')).data;
        if (!userPathways) {
            throw new Error("Unexpected data format received from server.");
        }
        //   const defaultPathways: Groups = (await axios.get('/tumors/api/v1/default-pathways')).data;
        //   if (!defaultPathways) {
        //     throw new Error("Unexpected data format received from server.");
        //   }
        //   const output: Groups = Object.assign({}, userPathways, defaultPathways);
        
        return userPathways;
    };
  
    const { data: pathways, isFetching: loading, error, refetch: queryRefetch } = useQuery('pathways', fetchPathways, {
      refetchOnWindowFocus: false,
    });
  
    return { pathways: pathways ?? {}, loading, error, refetch: queryRefetch };
}

export const usePlotGroups = (versionTypes: number[]) => {
    const queryClient = useQueryClient();
    const versionTypeList = versionTypes || [];

    // Fetch plot groups function
    const fetchPlotGroups = async (versionQueryString: string): Promise<PlotGroup[]> => {
      const response = await axios.get(`/tumors/api/v1/plotgroups?versionTypes=${versionQueryString}`);
      if (!response.data) {
        throw new Error("Unexpected data format received from server.");
      }
      return response.data;
    };
  

    const { data: plotGroups = [], isLoading: loading, error, refetch: queryRefetch } = useQuery<PlotGroup[], Error>(
        ['plotGroups', versionTypeList.join(',')], 
        () => fetchPlotGroups(versionTypeList.join(',')),
        { 
          enabled: versionTypeList.length > 0,
          refetchOnWindowFocus: false,
          select: data => {
                const existingData = queryClient.getQueryData<PlotGroup[]>(['plotGroups', versionTypeList.join(',')]);
                return isEqual(data, existingData) ? existingData ?? data : data;
           }

        }
    );
    
    const flatPlotDict: PlotDict = useDeepCompareMemo(() => {
        const dict: PlotDict = {}
        plotGroups.forEach(group => {
          group.plots.forEach(plot => {
            dict[plot.id] = plot;
          });
        });
        return dict;
      }, [plotGroups]);
  
    // Delete plot group
    const deletePlotGroup = useMutation(async (id: number) => {
      const csrfToken = getCookie("csrftoken"); // Ensure getCookie is defined or imported
      await axios.delete(`/tumors/api/v1/plotgroup/${id}`, {
        headers: {
          "X-CSRFToken": csrfToken,
        },
      });
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries('plotGroups');
      },
    });
  
    const saveNewPlotGroup = useMutation(async ({ name, isPublic }: { name: string; isPublic: boolean }) => {
        const csrfToken = getCookie("csrftoken"); // Ensure getCookie is defined or imported
        return axios.post('/tumors/api/v1/plotgroups', { name, public: isPublic }, {
            headers: {
                "X-CSRFToken": csrfToken,
            },
        });
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries('plotGroups');
        },
        onError: (error) => {
            console.error("Failed to create plot group", error);
            // Optionally, handle the error more gracefully, e.g., displaying a user-friendly message
        }
    });
    
    const addNewPlotGroup = (name: string, isPublic: boolean) => {
        saveNewPlotGroup.mutateAsync({ name, isPublic });
    };
  
    return { 
      plotGroups, 
      loading, 
      error, 
      refetch: queryRefetch,
      add: addNewPlotGroup, 
      delete: deletePlotGroup.mutateAsync, 
      flatPlotDict 
    };
};
  

const convertApiDataToSelectedItems = (apiData: ApiData) => {

    let processedSelectedItems: SelectedItems = {
        'X-Axis Data': [],
        'Y-Axis Data': [],
        'Z-Axis Data': []
    };
    
    for (let axis in apiData.axes) {
        if (axis === 'x') {
            processedSelectedItems['X-Axis Data'] = apiData.axes[axis]
        }
        else if (axis === 'y') {
            processedSelectedItems['Y-Axis Data'] = apiData.axes[axis]
        }
        else if (axis === 'z') {
            processedSelectedItems['Z-Axis Data'] = apiData.axes[axis]
        }
    }

    return processedSelectedItems;
};

const extractPlotValues = (axisName: string, selectedItems: {[key: string]: AxisItem[]}) => {
    
    let axis = axisName === 'X-Axis Data' ? 'x' : axisName === 'Y-Axis Data' ? 'y' : 'z';

    let method: string = '';
    let categoricalValues: {[key: string]: any[]} = {};
    let otherValues: {[key: string]: {values: any[], aggregationMethod: any, groupBy: any, filterItems?: any, normalizeBy?: string, 
        normalizeUnits?: string, references?: Reference[], relativeUnits?:string, processingMode?: 'absolute'|'relative', 
        hideReference?: boolean, groupByRelative?: any, normReferences?: Reference[], normRelativeUnits?: string,
        normHideReference?: boolean, groupByNormRelative?: string[], pathwayMode?: boolean, pathwayScore?: string}} = {};

    for (let item of selectedItems[axisName]) {
        if (method === '') {
            method = item.method;
        } else if (method !== item.method) {
            method = 'mixed';
        }
        if (['categorical', 'index'].includes(item.method)) {
            categoricalValues[item.name] = (item.values.length > 1 || item.values[0] !== "") ? item.values : [];
        } else {
            if (!(item.name in otherValues)) {
                otherValues[item.name] = {
                    'values': [], 'aggregationMethod': item.aggregationMethod, 'groupBy': item.groupBy, 'filterItems': item.filterItems, 
                    'normalizeBy': item.normalizeBy, 'normalizeUnits': item.normalizeUnits, 'references': item.references, 'relativeUnits': item.relativeUnits, 'processingMode': item.processingMode, 
                    'hideReference': item.hideReference, 'groupByRelative': item.groupByRelative, 'normReferences': item.normReferences, 'normRelativeUnits': item.normRelativeUnits,
                    'normHideReference': item.normHideReference, 'groupByNormRelative': item.groupByNormRelative, 'pathwayMode': item.pathwayMode, 'pathwayScore': item.pathwayScore
                };
            }
            otherValues[item.name]['values'].push(...item.values);
        }
    }

        
    return { axis: axis, method: method, categoricalValues: categoricalValues, otherValues: otherValues };
};

export const processSelectedItems = (selectedItems: SelectedItems, categoricalData: any): ProcessedItems => {
    let data: {[axis: string]: any} = {};
    let categories: {[axis: string]: any} = {};
    
    if (!categoricalData) {
        return { data, categories };
    }

    for (let item of Object.keys(selectedItems)) {
        let itemDict = extractPlotValues(item, selectedItems);
        let axis = itemDict.axis
        if (['categorical', 'index'].includes(itemDict.method)) {
            if (axis === 'z') {
                continue; // Skip z-axis if it's categorical or index
            }
            if (!(axis in categories)) {
                categories[axis] = {};
            }

            for (let cat in itemDict.categoricalValues) {

                if (!(cat in categories[axis])) {
                    categories[axis][cat] = {};
                }
                
                let nameToLookup = itemDict.categoricalValues[cat][0]
                if (cat + '|' + nameToLookup in categoricalData) {
                    categories[axis][cat][nameToLookup] = categoricalData[cat + '|' + nameToLookup]
                }
            }
        } else {
            if (!(axis in data)) {
                data[axis] = {};
            }

            for (let key in itemDict.otherValues) {
                if (!(key in data[axis])) {
                    data[axis][key] = {'values': [], 'aggregationMethod': null, 'groupBy': [], 'filterItems': []};
                }
                
                if (itemDict.otherValues[key]['groupBy']) {
                    data[axis][key]['groupBy'].push(...itemDict.otherValues[key]['groupBy']);
                }

                if (itemDict.otherValues[key]['filterItems']) {
                    data[axis][key]['filterItems'].push(...itemDict.otherValues[key]['filterItems']);
                }
                
                if (itemDict.otherValues[key]['aggregationMethod']) {
                    data[axis][key]['aggregationMethod'] = itemDict.otherValues[key]['aggregationMethod']
                }
                if (itemDict.otherValues[key]['values']) { 
                    data[axis][key]['values'].push(...itemDict.otherValues[key]['values']);
                }
                if (itemDict.otherValues[key]['normalizeBy']) {
                    data[axis][key]['normalizeBy'] = itemDict.otherValues[key]['normalizeBy'];
                }
                if (itemDict.otherValues[key]['normalizeUnits']) {
                    data[axis][key]['normalizeUnits'] = itemDict.otherValues[key]['normalizeUnits'];
                }
                if (itemDict.otherValues[key]['processingMode']) {
                    data[axis][key]['processingMode'] = itemDict.otherValues[key]['processingMode'];
                }
                if (itemDict.otherValues[key]['references']) {
                    data[axis][key]['references'] = itemDict.otherValues[key]['references'];
                }
                if (itemDict.otherValues[key]['relativeUnits']) {
                    data[axis][key]['relativeUnits'] = itemDict.otherValues[key]['relativeUnits'];
                }
                if (itemDict.otherValues[key]['hideReference'] !== undefined) {
                    data[axis][key]['hideReference'] = itemDict.otherValues[key]['hideReference'];
                }
                if (itemDict.otherValues[key]['groupByRelative'] !== undefined) {
                    data[axis][key]['groupByRelative'] = itemDict.otherValues[key]['groupByRelative'];
                }
                if (itemDict.otherValues[key]['normReferences']) {
                    data[axis][key]['normReferences'] = itemDict.otherValues[key]['normReferences'];
                }
                if (itemDict.otherValues[key]['normRelativeUnits']) {
                    data[axis][key]['normRelativeUnits'] = itemDict.otherValues[key]['normRelativeUnits'];
                }
                if (itemDict.otherValues[key]['normHideReference'] !== undefined) {
                    data[axis][key]['normHideReference'] = itemDict.otherValues[key]['normHideReference'];
                }
                if (itemDict.otherValues[key]['groupByNormRelative']) {
                    data[axis][key]['groupByNormRelative'] = itemDict.otherValues[key]['groupByNormRelative'];
                }
                if (itemDict.otherValues[key]['pathwayMode']) {
                    data[axis][key]['pathwayMode'] = itemDict.otherValues[key]['pathwayMode'];
                }
                if (itemDict.otherValues[key]['pathwayScore']) {
                    data[axis][key]['pathwayScore'] = itemDict.otherValues[key]['pathwayScore'];
                }
            }
        }
    }

    return { data, categories };
};



const usePrevious = <T>(value: T): T | undefined => {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

export const useGeneratePlots = (
    plotConfigs: Array<{
        plotConfig: PlotConfig,
        filtersState: OldFilterState,
        axisFiltersState: FilterState,
        advancedSortingState: any,
        selectedItems: any,
        categoricalData: any,
        fetchedObjectsChanged: any,
        tileState: TileState,
        tileSharedY: boolean,
        tileSharedX: boolean,
        setNumTileColumns: (cols: number) => void,
        groupedSampleMode: boolean,
        activeSampleGroupDict: any,
        plotId: number,
        groups: Groups,
        groupOrder: number[],
    }>,
    plotData: DataRow[], 
    onPlotSuccess: (plotIds: number[]) => void,
    onPlotError: (error: string, plotIds: number[]) => void,
    setLoadingStates: React.Dispatch<React.SetStateAction<boolean[]>>,
    setLoadingMessages: React.Dispatch<React.SetStateAction<string[]>>,
) => {

    const [plotAlerts, setPlotAlerts] = useState<any[]>(plotConfigs.map(() => []));
    const [plotLayouts, setPlotLayouts] = useState<any[]>(plotConfigs.map(() => ({})));
    const [plotTraces, setPlotTraces] = useState<any[]>(plotConfigs.map(() => []));

    const memoizedPlotConfigs = useMemo(() => plotConfigs, [JSON.stringify(plotConfigs)]);
    const memoizedPlotData = useMemo(() => plotData, [plotData]);

    const processedItems = useMemo(() => {
        return memoizedPlotConfigs.map(config => processSelectedItems(config.selectedItems, config.categoricalData));
    }, [memoizedPlotConfigs]);

    const previousValues = useRef<Array<any>>([]);
    
    

    const hasChanged = useCallback((previousValue: any, currentValue: any) => {
        if (previousValue === null || previousValue === undefined) return true;
        if (currentValue === null  || currentValue === undefined) return false;
        if (isObject(previousValue) && isObject(currentValue)){
            return !deepEqual(previousValue, currentValue);
        }

        return previousValue !== currentValue
    }, []);

    const defaultColorMap: Record<number, string> = useMemo(() => ({
        0: '#384b7f', 1: '#11b2f3', 2: '#ff6a6c', 3: '#a1a1a1',
        4: '#32cd5d', 5: '#2187d0', 6: '#8d34ff', 7: '#83f3fb',
        8: '#f6d45d', 9: '#f2cfcf', 10: '#f39a13', 11: '#f20d0d',
    }), []);


    const computePlotLayers = useCallback(async (
        plotIndex: number, 
        processedDataList: any, 
        xProcessedDataList: any, 
        yProcessedDataList: any, 
        zProcessedDataList: any,
        plotPValues: PValueDict[] | PValueDict[][],
        plotErrorBars: ErrorBarDict[] | ErrorBarDict[][],
        loadingCallback: (message: string) => void,
        colorOverrideMap: Record<string, string>
    ) => {
        // console.log('computing plot layers')

        const config = memoizedPlotConfigs[plotIndex];
        const { categories: categoryAxes, data: dataAxes } = processedItems[plotIndex];

        let filteredCategorical = filterCategories(categoryAxes, config.filtersState);

        let xValues = dataAxes['x'] ? dataAxes['x'] : {};
        let yValues = dataAxes['y'] ? dataAxes['y'] : {};
        let zValues = dataAxes['z'] ? dataAxes['z'] : {};
        let aggregationErrors: string = '';
        let tileTitleList: any = [];

        const plotType = config.plotConfig.plotType;
        let traces: any[] = [];

        let xProcessing = extractDataProcessing(xValues);
        let yProcessing = extractDataProcessing(yValues);
        let zProcessing = extractDataProcessing(zValues);
        let error = undefined;
        try {
            if (Object.keys(filteredCategorical).length === 0 && (Object.keys(xValues).length > 0 || Object.keys(yValues).length > 0 || Object.keys(zValues).length > 0)) {
                let newTraces = generateDataVsDataScatterPlot( xProcessedDataList, yProcessedDataList, xValues, yValues, plotType, config.plotConfig);
                traces = traces.concat(newTraces);
            } else {
                if (plotType === 'heatmap') {
                    
                    // ORIGINAL CODE
                    // let dataDict = processCategories(zValues, filteredCategorical, memoizedPlotData, config.axisFiltersState, xProcessing, yProcessing);
                    // traces.push(generateHeatmap(filteredCategorical, dataDict));

                    // console.log("filteredCategorical: ", filteredCategorical);
                    
                    // NOTE: If choosing custom analysis categorical data like 'Gene' on x or y axis, must have categorical filter created for Gene in the UI
                    let newTraces = await generateTiledPlots( processedDataList,  xValues, yValues, filteredCategorical, config.tileSharedX, generateHeatmapV2, config.plotConfig, config.selectedItems, loadingCallback);
                    traces = traces.concat(newTraces);

                } else if (plotType === 'bar') {
                    let newTraces  = await generateTiledPlots( processedDataList,  xValues, yValues, filteredCategorical, config.tileSharedX, generateBarPlot, config.plotConfig, plotPValues, config.tileSharedX, colorOverrideMap, plotErrorBars);
                    traces = traces.concat(newTraces);
                } else if (plotType === 'box') {
                    let newTraces  = await generateTiledPlots(processedDataList, xValues, yValues, filteredCategorical, config.tileSharedX, generateBoxPlot, config.plotConfig, plotPValues, config.tileSharedX, colorOverrideMap);
                    traces = traces.concat(newTraces);
                } else if (plotType === 'scatter') {
                    let newTraces  = await generateTiledPlots( processedDataList, xValues, yValues, filteredCategorical, config.tileSharedX, generateScatterPlot, config.plotConfig, yValues,);
                    traces = traces.concat(newTraces);
                } else if (plotType === 'line') {
                    let newTraces  = await generateTiledPlots( processedDataList, xValues, yValues, filteredCategorical, config.tileSharedX, generateScatterPlot, config.plotConfig, yValues, 'lines+markers');
                    traces = traces.concat(newTraces);
                } else {
                    throw Error('Plot type not supported');
                }
            }
        } catch (e: any) {
            console.log("Error generating plot layers: ", e);
            error = '' + e.message;
        }
        

        return {traces, error} ;
    }, [memoizedPlotConfigs, memoizedPlotData, processedItems]);

    const setLoadingMessagesDebounced = useMemo(() => {
        return debounce((index: number, message: string) => {
            setLoadingMessages(prevStates => {
                const newStates = [...prevStates];
                newStates[index] = message;
                return newStates;
            });
        }, 1);
    }, []);

    const setLoadingStatesDebounced = useMemo(() => {
        return debounce((index: number, loading: boolean) => {
            setLoadingStates(prevStates => {
                const newStates = [...prevStates];
                newStates[index] = loading;
                return newStates;
            });
        }, 1);
    }, []);

    const memoizedComputePlotLayers = useCallback((index: number, processedDataList: any, xProcessedDataList: any, yProcessedDataList: any, zProcessedDataList: any, plotPValues: PValueDict[] | PValueDict[][], plotErrorBars: ErrorBarDict[] | ErrorBarDict[][], loadingCallback: (s: string) => void, colorOverrideMap: Record<string, string>) => {
        return () => computePlotLayers(index, processedDataList, xProcessedDataList, yProcessedDataList, zProcessedDataList, plotPValues, plotErrorBars, loadingCallback, colorOverrideMap);
    }, [computePlotLayers]);

    const triggerComputation = useCallback(async (plotIndex: number) => {
        let computationError: string | undefined = undefined;
        console.log('triggering computation')
        const config = memoizedPlotConfigs[plotIndex];

        const { categories: categoryAxes, data: dataAxes } = processedItems[plotIndex];
        if (memoizedPlotData.length === 0) {
            console.log('No data available for plot index: ' + plotIndex);
            return { traces: [], layout:{} };
        }
        // console.time('generateProcessedDataList');
        const colorOverrideMap = structuredClone(config.plotConfig.colorOverride) || {};

        setLoadingMessagesDebounced(plotIndex, 'Generating Plot Data...');
        let { 
            dataList : processedDataList, 
            tileTitleList, 
            plotError,
            unprocessedDataList,
            xProcessedDataList,
            yProcessedDataList, 
            zProcessedDataList }  = await generateProcessedDataList(
                                        categoryAxes, 
                                        dataAxes, 
                                        config.filtersState,
                                        memoizedPlotData, 
                                        config.tileState, 
                                        config.axisFiltersState,
                                        [],
                                        config.activeSampleGroupDict,  
                                        config.groups, 
                                        config.groupOrder, 
                                        true, 
                                        config.plotConfig.barGroupBy,
                                        config.plotConfig.heatmapHandleFiltered
                                    ) // TODO add back sort order
        // console.timeEnd('generateProcessedDataList');

        // console.log("DEBUG - processedDataList: ", processedDataList);

        if (plotError) {
            computationError = plotError;
        }
        else if ((processedDataList !== null && processedDataList.length === 0) || (xProcessedDataList !== null && xProcessedDataList.length === 0)){
            computationError = "No data within selected filters";
        }
        // console.time('computePlotPValues');
        setLoadingMessagesDebounced(plotIndex, 'Computing P-Values...');
        let plotPValues = (processedDataList !== null) ? await computePlotPValues(processedDataList, unprocessedDataList, config.plotConfig) : [];
        setLoadingMessagesDebounced(plotIndex, 'Computing Error Bars...');
        
        let plotErrorBars = await computeErrorBars(processedDataList, unprocessedDataList, config.plotConfig, dataAxes); 
        // console.timeEnd('computePlotPValues');
        // console.time('computePlotLayers');
        
        setLoadingMessagesDebounced(plotIndex, 'Generating Plot Layers...');
        let {traces, error: layersError } = await computePlotLayers(
                                                    plotIndex, 
                                                    processedDataList, 
                                                    xProcessedDataList, 
                                                    yProcessedDataList, 
                                                    zProcessedDataList, 
                                                    plotPValues, 
                                                    plotErrorBars, 
                                                    (submessage) => setLoadingMessagesDebounced(plotIndex, 'Generating Plot Layers: ' + submessage), 
                                                    colorOverrideMap);
        if (layersError) {
            console.log('Error generating plot layers: ' + layersError);
            computationError = layersError;
        }

        // console.timeEnd('computePlotLayers');
        // console.time('updatePlotLayout');
        const minmax_values = getPlotLayersMinMax(traces);
        setLoadingMessagesDebounced(plotIndex, 'Generating Plot Layout...');
        let layout = updatePlotLayout(
            minmax_values,
            processedDataList,
            config.plotConfig, 
            plotPValues, 
            plotErrorBars,
            tileTitleList,
            config.setNumTileColumns, 
            config.tileSharedY,
            config.tileSharedX,
            config.selectedItems,
        );
        // console.timeEnd('updatePlotLayout');
        if (config.plotConfig.plotType === 'box' && config.plotConfig.boxpoints == 'all') {
            // console.time('setBoxPlotTicks');
            setLoadingMessagesDebounced(plotIndex, 'Setting Box Plot Ticks... ');
            [layout, traces] = setBoxPlotTicks(layout, traces, config.plotConfig, config.tileState, colorOverrideMap);
            // console.timeEnd('setBoxPlotTicks');
        }
        return {layout, traces, error:computationError};
    }, [memoizedPlotConfigs, memoizedPlotData, processedItems, memoizedComputePlotLayers]);

    useEffect(() => {
        if (previousValues.current.length !== memoizedPlotConfigs.length) {
            previousValues.current = memoizedPlotConfigs.map(() => ({}));
        }

        memoizedPlotConfigs.forEach((config, index) => {
            const prev = previousValues.current[index] || {};
            if (
                hasChanged(prev.selectedItems, config.selectedItems) ||
                hasChanged(prev.tileState, config.tileState) ||
                hasChanged(prev.tileSharedY, config.tileSharedY) ||
                hasChanged(prev.tileSharedX, config.tileSharedX) ||
                hasChanged(prev.plotConfig, config.plotConfig) ||
                hasChanged(prev.filtersState, config.filtersState) ||
                hasChanged(prev.axisFiltersState, config.axisFiltersState) ||
                // hasChanged(prev.numTileColumns, config.numTileColumns) ||
                hasChanged(prev.groupedSampleMode, config.groupedSampleMode) ||
                hasChanged(prev.activeSampleGroupDict, config.activeSampleGroupDict) ||
                hasChanged(prev.plotData, memoizedPlotData) || 
                hasChanged(prev.groups, config.groups) ||
                hasChanged(prev.groupOrder, config.groupOrder)
            ) {
                // if (memoizedPlotData.length === 0) {
                //     setLoadingStates(prevStates => {
                //         const newStates = [...prevStates];
                //         newStates[index] = false;
                //         return newStates;
                //     });
                //     onPlotError('No Data Within Selected Filters', [config.plotId]);
                //     previousValues.current[index] = { 
                //         ...config, 
                //         plotData: memoizedPlotData
                //     };
                //     return;
                // }
                setLoadingStatesDebounced(index, true);
            
                setTimeout(() => triggerComputation(index).then((data)=> {
                    const { layout, traces, error } = data
                    
                    setLoadingStatesDebounced(index, false);
                    setLoadingMessagesDebounced(index, 'Finalizing Plot...');
                    // console.timeEnd("Plot Generation");
                    
                    setPlotLayouts(prevLayouts => { 
                        const newLayouts = [...prevLayouts];
                        newLayouts[index] = layout;
                        return newLayouts;
                    });
                    setPlotTraces(prevLayers => {
                        const newLayers = [...prevLayers];
                        newLayers[index] = traces;
                        return newLayers;
                    });
                    if (error !== undefined && error !== '') {
                        console.log("Error: ", error);
                        onPlotError(error, [config.plotId]);
                    } else {
                        
                        onPlotSuccess([config.plotId]);
                    }
                }), 0);
            }
            previousValues.current[index] = { 
                ...config, 
                plotData: memoizedPlotData
            };
        });
    }, [memoizedPlotConfigs, memoizedPlotData, hasChanged]);
    
    const addAlert = useCallback((plotIndex: number, message: string, type: string, duration: number) => {
        if (!(type === 'success' || type === 'warning' || type === 'info' || type === 'error')) {
            return;
        }

        setPlotAlerts(prevAlerts => {
            const newAlerts = [...prevAlerts];

            if (!Array.isArray(newAlerts[plotIndex])) {
                newAlerts[plotIndex] = [];
            }

            const existingAlert = newAlerts[plotIndex].find(
                (a: any) => a.message === message && a.type === type
            );

            if (!existingAlert) {
                let alert = { 
                    message, 
                    type, 
                    duration, 
                    remove: () => { // callback to remove this alert
                        setPlotAlerts(prevAlerts => {
                            const updatedAlerts = [...prevAlerts];
                            updatedAlerts[plotIndex] = updatedAlerts[plotIndex].filter((a: any) => a !== alert);
                            return updatedAlerts;
                        });
                    }
                };
                newAlerts[plotIndex] = [...newAlerts[plotIndex], alert];
            }
            
            return newAlerts;
        });
    }, []);


    const memoizedAddAlert = useCallback((index: number) => {
        return (message: string, type: string, duration: number) => addAlert(index, message, type, duration);
    }, [addAlert]);

    const memoizedUpdatePlotConfiguration = useCallback((index: number) => {
        return () => triggerComputation(index);
    }, [triggerComputation]);

    const memoizedResults = useMemo(() => 
        memoizedPlotConfigs.map((_, index) => ({
            plotLayers: plotTraces[index],
            plotLayout: plotLayouts[index],
            categoryAxes: processedItems[index].categories,
            dataAxes: processedItems[index].data,
            plotAlerts: plotAlerts[index],
            plotId: memoizedPlotConfigs[index].plotId,
            // computePlotLayers: memoizedComputePlotLayers(index),
            addAlert: memoizedAddAlert(index),
            updatePlotConfiguration: memoizedUpdatePlotConfiguration(index),
        }))
    , [memoizedPlotConfigs, plotTraces, plotLayouts, processedItems, plotAlerts,
    memoizedComputePlotLayers, memoizedAddAlert, memoizedUpdatePlotConfiguration]);

    return memoizedResults;
};

export const useMultiPlot = (
    plotIds: number[],
    selectedPlotId: number | null,
    plotTypeDict: { [plotID: number]: string },
    initialPlotConfig: PlotConfig,
    setDataManipulation: (id: number | null) => void
    ) => {
    const [plotData, setPlotData] = useState<{
        [id: number]: {
        selectedItems: SelectedItems | null;
        configuration: PlotConfig;
        axisFilters: FilterState;
        selectedVersionTypes: number[];
        dataPipelines: any[];
        tileState: TileState;
        };
    }>({});
    
    const queryClient = useQueryClient();

    const plotQueries = useQueries(
        plotIds.map((id) => ({
        queryKey: ['plotTemplateDetails', id],
        queryFn: () => fetchPlotTemplateDetails(id),
        refetchOnWindowFocus: false,
        enabled: !!id,
        onSuccess: (data: any) => {
            if (data) {
                const processedSelectedItems = convertApiDataToSelectedItems(data);
                setDataManipulation(data.data_pipelines.length > 0 ? data.data_pipelines[0] : null);

                const updatedConfig: PlotConfig = {
                    ...initialPlotConfig,
                    ...data.settings,
                    xmin: data.settings.xmin ?? null,
                    xmax: data.settings.xmax ?? null,
                    ymin: data.settings.ymin ?? null,
                    ymax: data.settings.ymax ?? null,
                    name: data.name
                };

                for (let filterKey in data.filters) {
                    let filter = data.filters[filterKey];
                    if (!filter.hasOwnProperty('type')) {
                        filter.type = 'categorical';
                        filter.filterBehavior = 'include';
                    }
                }

                setPlotData((prevData) => ({
                    ...prevData,
                    [id]: {
                    selectedItems: processedSelectedItems,
                    configuration: updatedConfig,
                    axisFilters: Object.keys(data.filters).length > 0 ? data.filters : getAxisFiltersFromSelectedItems(processedSelectedItems) ,
                    dataPipelines: data.data_pipelines,
                    selectedVersionTypes: data.version_types,
                    tileState: data.tileState || {}
                    
                    },
                }));
            }
        },
        }))
    );
    
    async function fetchPlotTemplateDetails(plotId: number | null) {
        if (!plotId) return null;
        const response = await axios.get(`/tumors/api/v1/plottemplates/${plotId}`);
        return response.data;
    }
    
    const saveCustomPlot = (
        id: number,
        saveAs: boolean,
        plotSelectedItems: SelectedItems,
        plotAxisFilter: FilterState, 
        name: string,
        plotGroupId: number,
        isPublic: boolean,
        plotConfig: PlotConfig,
        dataPipelineId: number | null,
        pathways: SearchableDropdownOption[],
        tileState: TileState,
        onSaveSuccess: (id: number) => void,
        onSaveError?: (error: Error) => void
    ) => {
        const plotTypeDictFlipped: { [plotName: string]: number } = {};
        Object.entries(plotTypeDict).forEach(([key, value]) => {
            plotTypeDictFlipped[value] = parseInt(key);
        });
    
        let versionTypeList: number[] = [];
        let formattedSelectedItems: { [key: string]: AxisItem[] } = {};
        for (let key in plotSelectedItems) {
            for (let item of plotSelectedItems[key]) {
                if (item.versionType) {
                versionTypeList.push(item.versionType);
                }
            }
            formattedSelectedItems[key[0].toLowerCase()] = plotSelectedItems[key];
        }

        if (isPublic) {
            plotConfig.colorOverride = {}; // Ensure color overrides are cleared for public plots
        }

        const dataPipelineList: number[] = dataPipelineId !== null ? [dataPipelineId] : [];
        plotConfig.pathways = structuredClone(pathways);
        const apiData: ApiData = {
            name: name,
            settings: plotConfig,
            axes: formattedSelectedItems,
            filters: plotAxisFilter,
            public: isPublic,
            plot_group: plotGroupId,
            version_types: versionTypeList,
            data_pipelines: dataPipelineList,
            tileState: tileState
        };
    
        const csrfToken = getCookie("csrftoken");
    
        if (saveAs) {
            axios.post(`/tumors/api/v1/plottemplates`, apiData, { headers: { "X-CSRFToken": csrfToken } })
                .then((response) => {
                    onSaveSuccess(response.data.id);
                })
                .catch((err) => {
                    if (onSaveError) {
                        onSaveError(err);
                    }
                }
            );
        } else {
            axios.put(`/tumors/api/v1/plottemplates/${id}`, apiData, { headers: { "X-CSRFToken": csrfToken } })
                .then((response) => {
                    onSaveSuccess(response.data.id);
                })
                .catch((err) => {
                    if (onSaveError) {
                        onSaveError(err);
                    }
                }
            );
        }
    };
    
    const deleteCustomPlot = async (id: number) => {
        try {
            const csrfToken = getCookie("csrftoken");
            await axios.delete(`/tumors/api/v1/plottemplates/${id}`, {
                headers: {
                "X-CSRFToken": csrfToken,
                },
            });
            // console.log("Successfully deleted");
            // TODO: need to refresh custom plot component and flush local browser storage cache with stale plot ID
        } catch (error) {
            console.error("Failed to delete plot group", error);
        }
    };
    
    const refetchPlotDetails = (id: number) => {
        queryClient.invalidateQueries(['plotTemplateDetails', id]);
    };

    
    const resultData = useMemo(() => {
        return plotIds.reduce((acc, id) => {
            const query = plotQueries.find((q) => q?.data?.id === id);
            // const query = plotQueries.find((q) => (q as any).queryKey?.[1] === id);
            // console.log("Here is the query!", query)
            if (query === undefined){
                return acc;
            }
            // console.log("plot queries ", plotQueries)
            acc[id] = {
                selectedItems: plotData[id]?.selectedItems || null,
                axisFiltersState: plotData[id]?.axisFilters || {}, 
                selectedVersionTypes: plotData[id]?.selectedVersionTypes || [],
                isLoading: query?.isLoading ?? false,
                dataPipelines: plotData[id]?.dataPipelines || [],
                isError: query?.isError ?? false,
                error: query?.error || null,
                configuration: plotData[id]?.configuration || initialPlotConfig,
                tileState: plotData[id]?.tileState || {},
                saveCustomPlot: (
                    saveAs: boolean,
                    plotSelectedItems: SelectedItems,
                    plotAxisFilter: FilterState,
                    name: string,
                    plotGroupId: number,
                    isPublic: boolean,
                    plotConfig: PlotConfig,
                    dataPipelineId: number | null,
                    pathways: SearchableDropdownOption[],
                    tileState: TileState,
                    onSaveSuccess: (id: number) => void,
                    onSaveError?: (error: Error) => void
                ) =>
                    saveCustomPlot(
                        id,
                        saveAs,
                        plotSelectedItems,
                        plotAxisFilter,
                        name,
                        plotGroupId,
                        isPublic,
                        plotConfig,
                        dataPipelineId,
                        pathways,
                        tileState,
                        onSaveSuccess,
                        onSaveError
                    ),
                deleteCustomPlot: () => deleteCustomPlot(id),
                refetchPlotDetails: () => refetchPlotDetails(id),
            };
            return acc;
        }, {} as MultIPlotData);
    }, [plotQueries, plotData, initialPlotConfig]);
    
    return resultData;
};

export const useLoadDataframe = (
    sample: number[],
    versionType: number[],
    treatment: SearchableDropdownOption[],
    timepoint: SearchableDropdownOption[],
    dataManipulation: number | null,
) => {
    const [taskId, setTaskId] = useState<string | null>(null);
    const [taskCompleted, setTaskCompleted] = useState(true);
    const [loadedData, setLoadedData] = useState<any>({data: [], columns: [], categoricalColumns: []});
    const [initiatingFetch, setInitiatingFetch] = useState(false);
    const [progress, setProgress] = useState<number>(0);
    const [fetchError, setFetchError] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('Loading Initiated');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [progressTracker, setProgressTracker] = useState<number[]>([]);
    
    const initiateDataFetch = useCallback(() => {
        if (taskId && !initiatingFetch) {
            axios.post(`/tumors/api/v1/analyses/cancel-task/${taskId}/`, {}, {
                headers: { "X-CSRFToken": getCookie("csrftoken") }
            }).then(() => {
                setTaskId(null);
            }).catch(error => {
                console.error('Error canceling task:', error);
            });
        }
        
        setInitiatingFetch(true);
        setIsLoading(true);
        setMessage('Loading Initiated');
        setFetchError(false);
        if (sample.length > 0 && versionType.length > 0) {
            const csrfToken = getCookie("csrftoken");
            axios.post("/tumors/api/v1/analyses/initiate-fetch", {
                analysis_filters: {
                    samples: sample,
                    version_types: versionType,
                    treatments: treatment.map(t => t.id),
                    timepoints: timepoint.map(t => t.id),
                },
                index_only: false,
                data_manipulation: dataManipulation,
            }, { headers: { "X-CSRFToken": csrfToken } })
            .then(response => {
                setTaskId(response.data.task_id);
                setTaskCompleted(false); 
                setInitiatingFetch(false);
                setProgress(0);
            })
            .catch(error => {
                console.error('Error initiating fetch:', error);
            });
        }
    }, [sample, versionType, dataManipulation]);

    useEffect(() => {
        initiateDataFetch();
    }, [initiateDataFetch]);

    const { isError, error, isFetching: isCheckingStatus } = useQuery(
        ['taskStatus', taskId],
        () => axios.get(`/tumors/api/v1/analyses/check-task/${taskId}`, {
            headers: { "X-CSRFToken": getCookie("csrftoken") }
        }).then(res => res.data),
        {
            enabled: !!taskId && !fetchError,
            refetchOnWindowFocus: false,
            refetchInterval: (data) => { return (data && data.progress.status === 'running') ? 2000 : false},
            onError: (error) => {
                console.log('Error fetching data:', error);
                setFetchError(true);
                setTaskId(null);
            },
            onSuccess: (data) => {
                const { total, completed, status, message } = data.progress ?? { total: 1, completed: 0, status: 'running', message: '' }; // Default to prevent division by zero
                const progressPercentage = Math.min((completed / total) * 50, 50); // Cap progress at 50% if running
                if (status === 'running' && progressTracker.length !== 0 && message === 'Processing') {
                    setProgress(progressTracker[0] + 50);
                    setProgressTracker(prev => [...prev.slice(1)]);
                    setMessage(message);
                } else if (status === 'running') {
                    setProgress(progressPercentage);
                    setProgressTracker(prev => [...prev, progressPercentage]);
                    setMessage(message);
                }
                if (status === 'completed') {
                    setTaskCompleted(true);
                    setProgress(100);
                    setMessage('Data loaded successfully; Generating Plots...');
                }
            }
        }
    );

    const { data, isFetching: isFetchingData } = useQuery(
        ['fetchData', taskId],
        () => axios.get(`/tumors/api/v1/analyses/fetch-data/${taskId}`, {
            headers: { "X-CSRFToken": getCookie("csrftoken") }
        }).then(res => res.data),
        {
            enabled: !!taskId && taskCompleted,
            refetchOnWindowFocus: false,
            retry: 0,  // Disable automatic retry
            onSuccess: (data) => {
                setLoadedData(data);
                setProgress(100);
                setMessage('Data loaded successfully; Generating Plots...');
                setIsLoading(false);
                setTaskId(null);
            },
            onError: (error) => {
                console.error('Error fetching final data:', error);
                setFetchError(true);
                setTaskId(null);
            }
        }
    );

    
    let finalIsError = fetchError || isError;

    return {
        data: loadedData,
        progress,
        isError: finalIsError,
        error,
        taskCompleted,
        message,
        isLoading
    };
};

export const useLoadDataframeV2 = (
    sample: number[],
    versionType: number[],
    treatment: SearchableDropdownOption[],
    timepoint: SearchableDropdownOption[],
    dataManipulation: number | null,
    account_filter: number[],
    refresh: number,
    geneFilter: string[],
) => {
    const [taskId, setTaskId] = useState<string | null>(null);
    const [taskCompleted, setTaskCompleted] = useState(true);
    const [loadedData, setLoadedData] = useState<Data>({data: [], columns: {}, categoricalColumns: {}, numericColumns: {}});
    const [initiatingFetch, setInitiatingFetch] = useState(false);
    const [progress, setProgress] = useState<number>(0);
    const [fetchError, setFetchError] = useState<boolean>(false);
    const [responseError, setResponseError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [message, setMessage] = useState<string>('Loading Initiated');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [progressTracker, setProgressTracker] = useState<number[]>([]);

    const [refetchCounter, setRefetchCounter] = useState<number>(0);
    const maxRefetches = 3;
    const geneSampleMax = 40; // Maximum number of samples allowed for gene filter
    const sampleMax = 100; // Maximum number of samples allowed for any fetch

    const [internalGeneFilter, setInternalGeneFilter] = useState<string[] | undefined>(undefined);

    useEffect(() => {
        if (geneFilter.length > 0 && sample.length > geneSampleMax) {
            setInternalGeneFilter(geneFilter);
        } else {
            setInternalGeneFilter(undefined);
        }
    }, [geneFilter, sample]);

    const handleDownloadProgress = useCallback((event: AxiosProgressEvent) => {
        // console.log('Download progress:', event);
        const contentLength = event.event.srcElement?.getResponseHeader('X-Actual-Content-Length') || 'unknown';
        // console.log('Content-Length:', contentLength);
        if (contentLength !== 'unknown') {
            const estimatedUnzippedSize = parseInt(contentLength, 10);
            const percentCompleted = Math.round((event.loaded * 100) / (estimatedUnzippedSize ?? 1));
            // console.log('Percent Completed:', percentCompleted);
            if (percentCompleted >= 100) {
                setMessage('Data Retrieved; Building Plot...');
            }
            else {
                setMessage(`Downloading data...`);
            }
            setProgress(percentCompleted);
        } 

    }, []);

    const sortCategoricalColumns = (data: Data): Data => {
        // TO DO: only sorting by treatment_and_dose dropdown now; can sort by other filters in the future
        if (
            !data ||
            !data.categoricalColumns ||
            !Array.isArray(data.categoricalColumns['Treatment_and_Dose'])
        ) {
            return data;
        }

        const treatmentOrder = treatment.map(t => t.value);

        const sortedTreatments = [...data.categoricalColumns['Treatment_and_Dose']].sort((a, b) => {
            const aIndex = treatmentOrder.indexOf(a);
            const bIndex = treatmentOrder.indexOf(b);
            if (aIndex === -1 && bIndex === -1) return 0;
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });

        return {
            ...data,
            categoricalColumns: {
                ...data.categoricalColumns,
                Treatment_and_Dose: sortedTreatments
            }
        };
    };

    const initiateDataFetch = useCallback(() => {
        if (taskId && !initiatingFetch) {
            axios.post(`/tumors/api/v1/analyses/cancel-task/${taskId}/`, {}, {
                headers: { "X-CSRFToken": getCookie("csrftoken") },
                
            }).then(() => {
                setTaskId(null);
            }).catch(error => {
                console.error('Error canceling task:', error);
            });
        }
        if (sample.length > sampleMax) {
            setErrorMessage("Please select fewer samples. You've selected " + sample.length + ". Current maximum is " + sampleMax + " samples.");
            setResponseError(true);
            setIsLoading(false);
            setLoadedData({data: [], columns: {}, categoricalColumns: {}, numericColumns: {}});
            setTaskId(null); 
            return;
        }
        
        if (sample.length > 0 && versionType.length > 0 && sample.length <= sampleMax) {
            setInitiatingFetch(true);
            setFetchError(false);
            setResponseError(false);
            setIsLoading(true);
            setTimeout(() => {}, 0); // Allow state updates to propagate
            setMessage('Loading Initiated');
            setProgressTracker([]);
            const csrfToken = getCookie("csrftoken");
            axios.get("/tumors/api/v1/analyses/fetch", {
                params: {
                    samples: sample.join(','), 
                    version_types: versionType.join(','), 
                    timepoints: timepoint.map(t => t.id).join(','), 
                    data_manipulation: dataManipulation,
                    account_filter: account_filter.length > 0 ? account_filter.join(',') : undefined,
                    clear_cache: refresh > 0 ? true : false,    // Clear server cache if user clicks refresh button
                    gene_filter: internalGeneFilter,
                },
                headers: { "X-CSRFToken": csrfToken },
                onDownloadProgress: handleDownloadProgress,
            })
            .then(response => {
                if (response.data === '') {
                    setErrorMessage("Data is too large so the browser cannot handle it. Please reduce the number of samples or version types.");
                    setResponseError(true);
                    setIsLoading(false);
                    setTaskId(null);
                    return;
                }
                if (response.data.status === 'done') {
                    setInitiatingFetch(false);
                    setLoadedData(
                        sortCategoricalColumns({
                            data: response.data.data,
                            columns: response.data.columns,
                            categoricalColumns: response.data.categorical_columns,
                            numericColumns: response.data.numeric_columns
                        } as Data)
                    );
                    setProgress(100);
                    setMessage('Data loaded successfully.');
                    setIsLoading(false);
                    setTaskCompleted(true); 
                    setTaskId(null);
                } else {
                    setTaskId(response.data.task_id);
                    setTaskCompleted(false); 
                    setInitiatingFetch(false);
                    setProgress(0);
                }
            })
            .catch(error => {
                console.error('Error initiating fetch:', error);
                setFetchError(true);
                setResponseError(error.message || 'Error initiating fetch');
                setTaskId(null);
                setIsLoading(false);
            });
        }
    }, [sample, versionType, dataManipulation, refresh, account_filter, internalGeneFilter, handleDownloadProgress]);

    useEffect(() => {
        setRefetchCounter(0);
    }, [sample, versionType, dataManipulation, refresh, account_filter, internalGeneFilter]);

    // useEffect(() => {
    //     initiateDataFetch();
    // }, [initiateDataFetch]);

    const { isError, error, isFetching: isCheckingStatus } = useQuery(
        ['taskStatus', taskId],
        () => axios.get(`/tumors/api/v1/analyses/check-task/${taskId}/`, {
            headers: { "X-CSRFToken": getCookie("csrftoken") }
        }).then(res => res.data),
        {
            enabled: !!taskId && !fetchError,
            refetchOnWindowFocus: false,
            refetchInterval: (data) => { return (data && (data.progress.status === 'running' || data.progress.status === 'pending')) ? 2000 : false},
            onError: (error) => {
                console.log('Error fetching data:', error);
                console.log("Retrying to fetch data...");
                // setTaskId(null);
                if (refetchCounter < maxRefetches) {
                    setRefetchCounter(prev => prev + 1);
                    initiateDataFetch(); // If we error, try to refetch data
                }
                else {
                    // setTaskCompleted(true);
                    setIsLoading(false);
                    setFetchError(true);
                    setErrorMessage("Error fetching data. Please try a different sample or analysis.");
                }
            },
            onSuccess: (data) => {
                const { total, completed, status, message } = data.progress ?? { total: 1, completed: 0, status: 'running', message: '' }; // Default to prevent division by zero
                const progressPercentage = Math.min((completed / total) * 80, 80); // Cap progress at 50% if running
                if (status === 'running' && progressTracker.length !== 0 && message === 'Processing') {
                    // console.log("Progress Tracker: ", structuredClone(progressTracker));
                    setProgress(Math.round((20*progressTracker[0]/80) + 80));
                    setProgressTracker(prev => [...prev.slice(1)]);
                    setMessage(message);
                } else if (status === 'running') {
                    setProgress(progressPercentage);
                    setProgressTracker(prev => [...prev, progressPercentage]);
                    setMessage(message);
                }
                if (status === 'completed') {
                    setTaskCompleted(true);
                    // setMessage('Downloading data...');
                    setProgressTracker([]);
                    setProgress(100);
                }
            }
        }
    );

    useEffect(() => {
        // console.log("sample", sample.length, "sampleMax", sampleMax, "taskId", taskId, "taskCompleted", taskCompleted, "fetchError", fetchError, "isCheckingStatus", isCheckingStatus, "versionType", versionType.length);
        if (sample.length > sampleMax) {
            console.log("Sample length exceeds maximum allowed samples: ", sample.length, " > ", sampleMax);
            setErrorMessage("Please select fewer samples. You've selected " + sample.length + ". Current maximum is " + sampleMax + " samples.");
            setLoadedData({data: [], columns: {}, categoricalColumns: {}, numericColumns: {}});
            setResponseError(true);
            setIsLoading(false);
            setTaskId(null);
            return;
        }
        if (taskId === null && !taskCompleted && !fetchError && !isCheckingStatus && sample.length > 0 && sample.length <= sampleMax && versionType.length > 0) {
            initiateDataFetch();
        }
    }, [initiateDataFetch, taskId, taskCompleted, fetchError, sample, versionType, refresh, dataManipulation, isCheckingStatus, account_filter, internalGeneFilter]);

    useEffect(() => {
        setTaskCompleted(false);
    }, [sample, versionType, refresh, dataManipulation, account_filter, internalGeneFilter])

    const { data, isFetching: isFetchingData } = useQuery(
        ['fetchData', taskId],
        () => axios.get(`/tumors/api/v1/analyses/fetch-data/${taskId}/`, {
            headers: { "X-CSRFToken": getCookie("csrftoken") },
            onDownloadProgress: handleDownloadProgress,
        }),//.then(res => res.data),
        {
            enabled: !!taskId && taskCompleted,
            refetchOnWindowFocus: false,
            retry: 0,  // Disable automatic retry
            onSuccess: (res) => {
                if (res.data === '') {
                    setErrorMessage("Data is too large so the browser cannot handle it. Please reduce the number of samples or version types.");
                    setResponseError(true);
                    setIsLoading(false);
                    setTaskId(null);
                    return;
                }
                console.log("Final data fetched: ", res);
                // console.log("Final data fetched: ", structuredClone(res.data));
                setLoadedData(
                    sortCategoricalColumns({
                        data: res.data.data,
                        columns: res.data.columns,
                        categoricalColumns: res.data.categorical_columns,
                        numericColumns: res.data.numeric_columns
                    } as Data)
                );
                setProgress(100);
                setMessage('Data loaded successfully.');
                setIsLoading(false);
                setTaskId(null);
            },
            onError: (error) => {
                console.error('Error fetching final data:', error);
                setResponseError(true);
                setErrorMessage('Error fetching final data: '+error);
                setTaskId(null);
            }
        }
    );

    
    let finalIsError = fetchError || isError || responseError;



    return {
        data: loadedData,
        progress,
        isError: finalIsError,
        error: errorMessage,
        taskCompleted,
        message,
        isLoading
    };
};

export const useDataPipelines = (sample: number[]) => {
    const fetchDataPipelines = async () => {
        const sampleQueryString = sample.join(',');
        const response = await axios.get(`/tumors/api/v1/data-pipelines?samples=${sampleQueryString}`)
        if (!response.data) {
            throw new Error("Unexpected data format received from server.");
        }
        return response.data
    }

    const { data, isError, error } = useQuery([sample], fetchDataPipelines, {
        refetchOnWindowFocus: false,
        enabled: sample.length > 0,
        retry: 1,
    });
    return { data, isError, error }
}

export const useBoardLayoutData = (boardId: number) => {
    const [data, setData] = useState<PageData>({});
    const [thumbnails, setThumbnails] = useState<Thumbnail[]>([]);
    const [title, setTitle] = useState<string>('');

    useEffect(() => {
        const getLayoutData = async (boardId: number) => {
            try {
                const response = await axios.get("/tumors/api/v1/get_analysis_board_layout/" + boardId);
                return response.data;
            } catch (err: any) {
                if (err.response && err.response.status === 403) {
                    // TODO change this to the normal link
                    window.location.href = '/accounts/login/?next=' + encodeURIComponent(window.location.pathname + window.location.search);
                }
                return {pageData: {}, title: ''};
            }
        }

        getLayoutData(boardId).then((data) => {
            setTitle(data.title);
            setData(data.pageData);
            setThumbnails(Object.keys(data.pageData).map(page => {
                return { src: `path/to/your/image${page}.jpg`, alt: `Page ${page}`, page: parseInt(page) };
            }));
        });
    }, [boardId]);

    return {
        data, 
        setData, 
        thumbnails, 
        setThumbnails, 
        title, 
        setTitle
      };
};



